<template>
  <div
    class="wrapper"
    :class="{ 'pos-fullheight': $route.meta.fullHeight }"
    v-on:touchstart="handleTouchStart"
    v-on:touchmove="handleTouchMove"
    v-on:touchend="handleTouchEnd"
  >
    <!-- Mobile Overlay -->
    <div
      class="sidebar-overlay"
      :class="{ 'show': isMobileSidebarOpen }"
      @click="closeSidebar"
    />

    <!-- Sidebar -->
    <left-menu
      :is-collapsed="isDesktopCollapsed"
      :is-mobile-open="isMobileSidebarOpen"
      @close-mobile="closeSidebar"
    />

    <div class="main">
      <!-- Navbar -->
      <top-menu @toggle-sidebar="toggleSidebar" />

      <!-- Content -->
      <main class="content px-3 pt-3">
        <license-banner />

        <div class="mb-3">
          <h3
            v-if="$route.meta.title"
            class="pos-page-title text-dark"
          >{{ $t($route.meta.title) }}</h3>
          <!-- แพ็กไม่มีฟีเจอร์ของหน้านี้ (เมนูซ่อนแล้ว แต่เข้าทาง URL / แท็บค้าง / แพ็กเปลี่ยนกลางคัน) -->
          <feature-not-available
            v-if="$route.meta.feature && !$hasFeature($route.meta.feature)"
            back-route="pos.table"
            back-text="_plan.Back to tables"
          />
          <slot v-else />
        </div>
      </main>
    </div>

    <!-- ตั้งค่าเครื่องพิมพ์ของเครื่องนี้ — เปิดจากไอคอนในเมนู/แถบบน และหน้าต่างถามครั้งแรก -->
    <offcanvas-printer-settings
      v-if="printerUi.open"
      @closed="printerUi.open = false"
    />
  </div>
</template>

<script>
import LeftMenu from './POS/LeftMenu.vue';
import TopMenu from './POS/TopMenu.vue';
import { initNotifySound } from '@/composables/useNotifySound';
import { useShopStore } from '@/stores/shop.store';
import {
  printerSettings,
  printerSettingsUi,
  printerSettingsReady,
  shouldPromptSetup,
  skipSetupPrompt,
  savePrinterSettings,
  openPrinterSettings
} from '@/utils/printerSettings';
import LicenseBanner from '@/components/license/LicenseBanner.vue';
import FeatureNotAvailable from '@/plugins/FeatureNotAvailable.vue';
import OffcanvasPrinterSettings from '@/components/printer-settings/OffcanvasPrinterSettings.vue';
import {
  bluetoothAdapterAvailable,
  reconnectBluetoothPrinter,
  requestBluetoothPrinter,
  shouldPromptBluetoothConnect,
  skipBluetoothConnectPrompt
} from '@/utils/bluetoothPrinter';

export default {
  name: "POS-LAYOUT",
  components: {
    LeftMenu,
    TopMenu,
    LicenseBanner,
    FeatureNotAvailable,
    OffcanvasPrinterSettings
  },
  data() {
    return {
      printerUi: printerSettingsUi,
      isDesktopCollapsed: false,
      isMobileSidebarOpen: false,
      touchStartX: 0,
      touchEndX: 0,
      minSwipeDistance: 50, // Minimum distance in px to register a swipe
    }
  },
  mounted() {
    // Unlock the shared notification sound on the first gesture in the POS shell
    // so it's already primed when pages (sessions, etc.) need to play it.
    initNotifySound();
    // sync flag ร้านล่าสุด (เช่น เปิด/ปิดระบบ delivery platform) โดยไม่ต้องออก/เข้าร้านใหม่
    useShopStore().refreshShopContext();
    this.checkScreenSize();
    window.addEventListener('resize', this.checkScreenSize);
    this.promptPrinterSetup();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkScreenSize);
  },
  methods: {
    /**
     * เครื่องที่ยังไม่เคยตั้งค่าเครื่องพิมพ์ → ถามครั้งเดียวตอนเข้า POS (docs/thermal-printer-setup.md
     * §Device printer settings) แต่ไม่บล็อกการขาย — เครื่องพิมพ์เสียกลางกะต้องยังรับเงินได้
     * "ไม่มีเครื่องพิมพ์" เป็นคำตอบจริง บันทึกแล้วไม่ถามอีก (มือถือเด็กเสิร์ฟตอบครั้งเดียวจบ)
     * "ไว้ทีหลัง" = ไม่ถามซ้ำจนกว่าจะปิดแท็บ
     *
     * รอ printerSettingsReady ก่อน — แคชเชียร์เดิมที่ถูกย้ายค่าให้อัตโนมัติต้องไม่เจอหน้าต่างนี้
     * เครื่องที่ตั้งเป็น Bluetooth ไว้แล้ว → ไปทางต่อเครื่องให้แทน (promptBluetoothConnect)
     */
    async promptPrinterSetup() {
      await printerSettingsReady;
      if (this.printerUi.open) return;
      if (!shouldPromptSetup()) {
        if (printerSettings.transport === 'bluetooth') await this.promptBluetoothConnect();
        return;
      }
      const result = await this.$swal.fire({
        icon: 'question',
        title: this.$t('_printer.Set up the printer for this device'),
        text: this.$t('_printer.Find the receipt printer connected to this device - you can change it later from the printer icon'),
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: this.$t('_printer.Set up now'),
        denyButtonText: this.$t('_printer.This device has no printer'),
        cancelButtonText: this.$t('_printer.Later'),
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-theme me-2',
          denyButton: 'btn btn-outline-secondary me-2',
          cancelButton: 'btn btn-light border'
        }
      });
      if (result.isConfirmed) openPrinterSettings();
      else if (result.isDenied) savePrinterSettings({ transport: 'none' });
      else skipSetupPrompt();
    },
    /**
     * Bluetooth ต้องมี "ตัวเครื่อง" ในหน้านี้ถึงจะพิมพ์ได้ — ลองต่อเงียบ ๆ ก่อน (เบราว์เซอร์ที่จำเครื่องได้)
     * ไม่ได้ค่อยถามให้กดเชื่อมต่อ 1 ครั้ง: ปุ่มใน swal คือ user gesture ที่หน้าต่างเลือกของ Chrome ต้องการ
     * ไม่ถามก็ได้ แต่แคชเชียร์จะไปรู้ตอนรับเงินแล้วใบเสร็จไม่ออก ("Receipt not printed")
     */
    async promptBluetoothConnect() {
      try {
        if (await reconnectBluetoothPrinter()) return;
      } catch (e) {
        // เครื่องพิมพ์ปิด/อยู่นอกระยะ — ถามให้กดเชื่อมต่อต่อ
      }
      // PC ที่ถอด dongle/ปิด Bluetooth ไปแล้ว — ถามไปก็กดต่อไม่ได้ ไอคอนจุดแดงบอกอยู่แล้ว
      if (!shouldPromptBluetoothConnect() || !(await bluetoothAdapterAvailable())) return;
      const result = await this.$swal.fire({
        icon: 'info',
        title: this.$t('_printer.Connect the Bluetooth printer'),
        text: this.$t('_printer.Turn on the printer then press Connect and choose it from the list'),
        showCancelButton: true,
        confirmButtonText: this.$t('_printer.Connect'),
        cancelButtonText: this.$t('_printer.Later'),
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-theme me-2',
          cancelButton: 'btn btn-light border'
        }
      });
      if (!result.isConfirmed) {
        skipBluetoothConnectPrompt();
        return;
      }
      try {
        const device = await requestBluetoothPrinter();
        this.$toast.success(this.$t('Printer selected') + ': ' + (device.name || 'Bluetooth'));
      } catch (e) {
        if (e && e.name === 'NotFoundError') return; // ปิดหน้าต่างเลือก — ไอคอนยังมีจุดแดงให้กดทีหลัง
        this.$toast.error(this.$t('_printer.Bluetooth printer not connected') + ': ' + (e.message || e));
      }
    },
    checkScreenSize() {
      if (window.innerWidth < 992) {
        // Mobile mode: Default closed
        this.isMobileSidebarOpen = false;
        this.isDesktopCollapsed = false; // Reset desktop state
      } else {
        // Desktop mode: Reset mobile state
        this.isMobileSidebarOpen = false;
      }
    },
    toggleSidebar() {
      if (window.innerWidth < 992) {
        this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
      } else {
        this.isDesktopCollapsed = !this.isDesktopCollapsed;
      }
    },
    closeSidebar() {
      this.isMobileSidebarOpen = false;
    },
    openSidebar() {
      this.isMobileSidebarOpen = true;
    },
    // --- Swipe Logic ---
    handleTouchStart(e) {
      this.touchStartX = e.changedTouches[0].screenX;
    },
    handleTouchMove(e) {
      // Optional: Prevent scrolling while swiping sidebar if needed
    },
    handleTouchEnd(e) {
      this.touchEndX = e.changedTouches[0].screenX;
      this.checkSwipeDirection();
    },
    checkSwipeDirection() {
      const distance = this.touchEndX - this.touchStartX;
      const isMobile = window.innerWidth < 992;

      if (!isMobile) return;

      // Swipe Right (Open) - Only if starting near left edge (optional refinement)
      if (distance > this.minSwipeDistance) {
        if (this.touchStartX < 50) {
           this.openSidebar();
        }
      }

      // Swipe Left (Close)
      if (distance < -this.minSwipeDistance) {
        if (this.isMobileSidebarOpen) {
          this.closeSidebar();
        }
      }
    }
  }
}
</script>

<style scoped>
/*
  Full-height "app shell" for pages that opt in via route meta.fullHeight
  (currently the order/menu screen). The base template makes .main a flex
  column with min-height:100vh + overflow:hidden — that min-height lets it grow
  past the viewport when the page content is tall, which makes the document
  body scroll (a second scrollbar competing with the product list). Here we pin
  the shell to exactly the viewport height and let the flex chain fill it, so
  the only scroller is the product list's own .menu-scroll inside the page.
  Scoped + route-gated, so every other POS page keeps its normal document
  scroll.
*/
.pos-fullheight {
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
}
.pos-fullheight .main {
  height: 100vh;
  height: 100dvh;
  min-height: 0;          /* override the template's min-height:100vh */
}
.pos-fullheight .content {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.pos-fullheight .content > .mb-3 {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  margin-bottom: 0 !important;   /* trailing margin would re-introduce overflow */
}
/* The page (slot) root fills the remaining space; :deep reaches the child
   component's root element. */
.pos-fullheight :deep(.pos-order-container) {
  flex: 1 1 auto;
  min-height: 0;
}
</style>

